import React, { useState } from "react";
import Icon from "./icons";
import { resultCards } from "../../../lib/artemis/present";
import { UserSkillCard } from "./userSkills";
import { SavedAlertCard } from "./alertCards";
import { ClimateChart } from "../../hunt-planner/huntPlannerComponents/huntPlanner-right-toolbar/unit-info/ClimateChart";
import { DeerActivityView } from "../../main-ui/common/hunt-wind/deer-activity";

// Structured data gets structured UI (API-3638): open hunts from get_regulations and draw windows
// from get_application_periods render as cards, beside the answer that cites them. The unit, hunt,
// weather and deer activity cards (API-3884..3891) reuse Hunt Planner's and HuntWind's pieces where
// those draw the same data.

const ExternalLink = ({ href, children }) => (
  <a className="artemis-card-link" href={href} target="_blank" rel="noopener noreferrer">
    {children}<Icon name="external" size={13} />
  </a>
)

const RegulationsCard = ({ card }) => (
  <section className="artemis-hunt-card" aria-label={card.title}>
    <div className="artemis-hunt-card-header">
      <Icon name="reg" size={15} />
      <span className="artemis-hunt-card-title">{card.title}</span>
    </div>
    {card.hunts.length > 0 && (
      <div className="artemis-hunt-grid">
        {card.hunts.map(hunt => (
          <React.Fragment key={hunt.key}>
            <span className="artemis-hunt-name">{hunt.name}</span>
            <span className="artemis-hunt-dates">{hunt.dates}</span>
            {hunt.tag
              ? <span className={`artemis-hunt-tag ${hunt.tag.draw ? 'artemis-hunt-tag-draw' : ''}`}>{hunt.tag.label}</span>
              : <span />}
          </React.Fragment>
        ))}
      </div>
    )}
    {card.moreHunts > 0 && <div className="artemis-hunt-more">+{card.moreHunts} more {card.moreHunts === 1 ? 'hunt' : 'hunts'}</div>}
    {card.rule && <div className="artemis-hunt-rule">{card.rule}</div>}
    <div className="artemis-hunt-card-footer">
      <span className="artemis-flex">{card.footer}{card.truncated ? ' · first results only' : ''}</span>
      {card.bookletUrl && <ExternalLink href={card.bookletUrl}>Open booklet</ExternalLink>}
    </div>
  </section>
)

const DrawCard = ({ card }) => (
  <section className="artemis-hunt-card" aria-label={card.title}>
    <div className="artemis-hunt-card-header">
      <Icon name="calendar" size={15} />
      <span className="artemis-hunt-card-title">{card.title}</span>
      {card.badge && <span className="artemis-hunt-badge">{card.badge}</span>}
    </div>
    <ol className="artemis-draw-list">
      {card.milestones.map(m => (
        <li key={m.key} className={m.past ? 'artemis-draw-past' : ''}>
          <span className={`artemis-draw-dot ${m.estimated ? '' : 'artemis-draw-dot-set'}`} aria-hidden="true" />
          <span className="artemis-flex">
            {m.label}
            {m.estimated && <span className="artemis-draw-estimated">estimated</span>}
          </span>
          <span className="artemis-draw-date">{m.date}</span>
        </li>
      ))}
    </ol>
    {(card.footer || card.applyUrl) && (
      <div className="artemis-hunt-card-footer artemis-hunt-card-footer-rule">
        <span className="artemis-flex">{card.footer}</span>
        {card.applyUrl && <ExternalLink href={card.applyUrl}>Apply</ExternalLink>}
      </div>
    )}
  </section>
)

// Hunt data cards (draw odds, units, harvest, terrain, viewshed) --------------------------------

const Card = ({ icon, title, badge, children, tone = 'hunt' }) => (
  <section className={`artemis-hunt-card artemis-card-tone-${tone}`} aria-label={title}>
    <div className="artemis-hunt-card-header">
      <Icon name={icon} size={15} />
      <span className="artemis-hunt-card-title">{title}</span>
      {badge && <span className="artemis-hunt-badge">{badge}</span>}
    </div>
    {children}
  </section>
)

const Bars = ({ rows }) => (
  <div className="artemis-bars">
    {rows.map(r => (
      <div key={r.key} className="artemis-bar-row" title={r.meaning || undefined}>
        <span className="artemis-bar-label">{r.label}</span>
        <span className="artemis-bar-track" aria-hidden="true"><span style={{ width: `${r.percent}%` }} /></span>
        <span className="artemis-bar-value">{r.value}</span>
      </div>
    ))}
  </div>
)

const Figures = ({ rows }) => (
  <div className="artemis-figures">
    {rows.map(f => <span key={f.label} className="artemis-figure"><strong>{f.value}</strong> {f.label}</span>)}
  </div>
)

const Upgrade = ({ text, onUpgrade }) => (
  <div className="artemis-card-upgrade">
    <Icon name="lock" size={14} />
    <span className="artemis-flex">{text}</span>
    {onUpgrade && <button type="button" className="artemis-small-button artemis-small-button-warn" onClick={onUpgrade}>Upgrade</button>}
  </div>
)

const Footer = ({ focus, cardKey, overlay, onAsk, others, children }) => {
  if (!focus && !(others && others.length) && !children) return null
  const showing = overlay.key === cardKey
  return (
    <div className="artemis-hunt-card-footer artemis-hunt-card-footer-rule">
      {others && others.length > 0 && (
        <span className="artemis-flex artemis-did-you-mean">
          <span>Did you mean</span>
          {others.map(o => <button key={o.key} type="button" className="artemis-reason" onClick={() => onAsk(o.ask)}>{o.label}</button>)}
        </span>
      )}
      {!(others && others.length) && <span className="artemis-flex">{children}</span>}
      {focus && (
        <button type="button" className="artemis-small-button artemis-small-button-go" aria-pressed={showing}
          onClick={() => (showing ? overlay.hide() : overlay.show(cardKey, focus))}>
          {showing ? 'Hide from map' : 'Show on map'}
        </button>
      )}
    </div>
  )
}

const DrawOddsCard = ({ card, cardKey, overlay, onAsk }) => (
  <Card icon="chart" title={card.title} badge={card.points}>
    <ul className="artemis-odds-list">
      {card.hunts.map(h => (
        <li key={h.key}>
          <span className="artemis-flex">
            <span className="artemis-hunt-name">{h.name}</span>
            <span className="artemis-hunt-sub">{[h.code, h.dates].filter(Boolean).join(' · ')}</span>
            {h.tablesNote && <span className="artemis-hunt-sub">{h.tablesNote}</span>}
            {h.latest.length > 0 && <Figures rows={h.latest} />}
          </span>
          {h.odds
            ? (
              <span className="artemis-odds">
                <strong>{h.odds}</strong>
                <span>{h.oddsLabel}{h.estimated ? ' · est.' : ''}</span>
              </span>
            )
            : h.oddsNote && <span className="artemis-hunt-sub artemis-odds-note">{h.oddsNote}</span>}
        </li>
      ))}
    </ul>
    {card.more > 0 && <div className="artemis-hunt-more">+{card.more} more {card.more === 1 ? 'hunt' : 'hunts'}</div>}
    <Footer cardKey={cardKey} overlay={overlay} onAsk={onAsk} others={card.others}>{card.truncated ? 'First results only' : 'Odds are percent drawn'}</Footer>
  </Card>
)

// Text that folds after a few lines, with Show more.
const CLAMP_AT = 220
const Clamp = ({ text }) => {
  const [open, setOpen] = useState(false)
  if (!text) return null
  const long = text.length > CLAMP_AT + 40
  return (
    <div className="artemis-card-text">
      {long && !open ? `${text.slice(0, CLAMP_AT).trim()}…` : text}
      {long && <button type="button" className="artemis-text-more" onClick={() => setOpen(!open)}>{open ? 'Show less' : 'Show more'}</button>}
    </div>
  )
}

// Labelled notes, only those with text: [[label, text], ...]
const Notes = ({ rows }) => rows.filter(([, text]) => text).map(([label, text]) => (
  <div key={label} className="artemis-card-note">
    <span className="artemis-card-note-label">{label}</span>
    <Clamp text={text} />
  </div>
))

const SourceLinks = ({ sources, label = 'Source' }) => (sources && sources.length > 0 ? (
  <div className="artemis-hunt-sub artemis-card-sources">
    {label}: {sources.map((s, i) => (
      <React.Fragment key={`${s.url || s.name}-${i}`}>
        {i > 0 && ' · '}
        {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer">{s.name}</a> : s.name}
      </React.Fragment>
    ))}
  </div>
) : null)

// One side of the unit's terrain (public or private land): elevation and the slope mix.
const TerrainSide = ({ title, side }) => (
  <div className="artemis-terrain-side">
    <span className="artemis-terrain-title">{title}</span>
    {side
      ? (
        <>
          {side.elevation && <span className="artemis-terrain-elev">{side.elevation}</span>}
          {(side.median || side.slope) && <span className="artemis-hunt-sub">{[side.median, side.slope].filter(Boolean).join(' · ')}</span>}
          {side.slopes.length > 0 && <Bars rows={side.slopes} />}
        </>
      )
      : <span className="artemis-hunt-sub">None in this unit</span>}
  </div>
)

const UnitInfoCard = ({ card, cardKey, overlay, onAsk, onUpgrade }) => (
  <Card icon="map" title={card.title} tone="map">
    {card.facts.length > 0 && <div className="artemis-card-facts">{card.facts.join(' · ')}</div>}
    {card.about && (card.about.overview || card.about.description || card.about.boundary) && (
      <div className="artemis-card-section">
        <span className="artemis-eyebrow">About this unit</span>
        {card.about.overview && <div className="artemis-card-text">{card.about.overview}</div>}
        <Clamp text={card.about.description} />
        <Notes rows={[['Boundary', card.about.boundary], ['Terrain', card.about.terrainNotes], ['Access', card.about.accessNotes], ['Weather', card.about.weatherNotes]]} />
        <SourceLinks sources={card.about.sources} />
      </div>
    )}
    {card.terrain && (
      <div className="artemis-card-section">
        <span className="artemis-eyebrow">Terrain</span>
        <div className="artemis-terrain-sides">
          <TerrainSide title="Public land" side={card.terrain.public} />
          <TerrainSide title="Private land" side={card.terrain.private} />
        </div>
      </div>
    )}
    {card.ownership.length > 0 && (
      <div className="artemis-card-section"><span className="artemis-eyebrow">Land ownership</span><Bars rows={card.ownership} /></div>
    )}
    {card.scores.rows.length > 0 && <div className="artemis-card-section"><span className="artemis-eyebrow">Scores</span><Figures rows={card.scores.rows} /></div>}
    {card.harvest.rows.length > 0 && <div className="artemis-card-section"><span className="artemis-eyebrow">Harvest{card.harvest.year ? ` · ${card.harvest.year}` : ''}</span><Figures rows={card.harvest.rows} /></div>}
    {card.locked && <Upgrade text="Scores and harvest need hunt data" onUpgrade={onUpgrade} />}
    {card.seasons.length > 0 && (
      <div className="artemis-card-section">
        <span className="artemis-eyebrow">Seasons</span>
        {card.seasons.map(s => (
          <div key={s.key} className="artemis-card-line">
            <span>{s.name}</span>
            <span>{s.dates}{s.tag && <span className={`artemis-hunt-tag ${s.tag === 'Draw' ? 'artemis-hunt-tag-draw' : ''}`}>{s.tag}</span>}</span>
          </div>
        ))}
        {card.moreSeasons > 0 && <div className="artemis-hunt-more artemis-hunt-more-inline">+{card.moreSeasons} more {card.moreSeasons === 1 ? 'season' : 'seasons'}</div>}
      </div>
    )}
    <Footer focus={card.focus} cardKey={cardKey} overlay={overlay} onAsk={onAsk} others={card.others} />
  </Card>
)

const UnitsAtPointCard = ({ card }) => (
  <Card icon="pin" title={card.title} tone="map">
    <div className="artemis-card-section">
      {card.units.map(u => <div key={u.key} className="artemis-card-line"><span>{u.species}</span><span>{u.name}</span></div>)}
      {card.more > 0 && <div className="artemis-hunt-more">+{card.more} more</div>}
    </div>
  </Card>
)

const HarvestCard = ({ card, cardKey, overlay, onAsk }) => (
  <Card icon="chart" title={card.title}>
    <div className="artemis-harvest-grid">
      <span className="artemis-eyebrow">Year</span><span className="artemis-eyebrow">Harvest</span><span className="artemis-eyebrow">Best success</span>
      {card.years.map(y => (
        <React.Fragment key={y.year}>
          <span>{y.year}{y.estimated && <span className="artemis-draw-estimated">est.</span>}</span>
          <span>{y.total || '—'}</span>
          <span>{y.success || '—'}</span>
        </React.Fragment>
      ))}
    </div>
    {card.noTotal && card.rows.length > 0 && (
      <div className="artemis-card-section">
        <span className="artemis-eyebrow">{card.rowsYear} by hunt · no published total</span>
        {card.rows.map(r => <div key={r.key} className="artemis-card-line"><span>{r.name}</span><span>{[r.harvest && `${r.harvest} taken`, r.success].filter(Boolean).join(' · ')}</span></div>)}
      </div>
    )}
    <Footer cardKey={cardKey} overlay={overlay} onAsk={onAsk} others={card.others}>{card.noTotal ? 'Hunts overlap, so they aren\u2019t added up' : null}</Footer>
  </Card>
)

const TerrainCard = ({ card, cardKey, overlay, onAsk }) => (
  <Card icon="terrain" title={card.title} tone="map">
    {card.facts.length > 0 && <div className="artemis-card-facts">{card.facts.join(' · ')}</div>}
    {card.slopes.length > 0 && <div className="artemis-card-section"><span className="artemis-eyebrow">Slope</span><Bars rows={card.slopes} /></div>}
    {card.aspects.length > 0 && <div className="artemis-card-facts">Facing {card.aspects.join(', ')}</div>}
    {card.match && (
      <div className="artemis-card-section">
        <span className="artemis-eyebrow">What you asked for</span>
        <div className="artemis-card-facts">{[card.match.summary, card.match.elevation].filter(Boolean).join(' · ')}</div>
      </div>
    )}
    <Footer focus={card.focus} cardKey={cardKey} overlay={overlay} onAsk={onAsk} />
  </Card>
)

const ViewshedCard = ({ card, cardKey, overlay, onAsk }) => (
  <Card icon="eye" title={card.title} badge={card.visible ? `${card.visible} visible` : null} tone="map">
    {card.facts.length > 0 && <div className="artemis-card-facts">{card.facts.join(' · ')}</div>}
    <Footer focus={card.focus} cardKey={cardKey} overlay={overlay} onAsk={onAsk}>What you could see from here</Footer>
  </Card>
)

const WeatherCard = ({ card }) => (
  <Card icon="sun" title={card.title} tone="map">
    {card.days.length > 0 && (
      <div className="artemis-weather-days">
        {card.days.map(d => (
          <div key={d.key} className="artemis-weather-day">
            <span className="artemis-weather-name">{d.name}</span>
            <span className="artemis-weather-cond">{d.weather}</span>
            <span className="artemis-weather-temp">{d.high != null ? `${d.high}°` : ''}{d.low != null ? <span> / {d.low}°</span> : ''}</span>
            <span className="artemis-weather-meta">{[d.precip != null ? `${d.precip}% precip` : null, d.wind].filter(Boolean).join(' · ')}</span>
          </div>
        ))}
      </div>
    )}
    {(card.sun || card.moon) && (
      <div className="artemis-card-section">
        {card.sun && <div className="artemis-weather-line"><Icon name="sun" size={14} /><span>{card.sun}</span></div>}
        {card.moon && <div className="artemis-weather-line"><Icon name="moon" size={14} /><span>{card.moon}</span></div>}
      </div>
    )}
    {card.hours.length > 0 && (
      <div className="artemis-card-section">
        <span className="artemis-eyebrow">Wind, next 24 hours</span>
        <div className="artemis-wind-strip">
          {card.hours.map(h => (
            <div key={h.key} className={`artemis-wind-hour ${h.isDay ? '' : 'artemis-wind-night'}`}
              title={[h.direction && `From ${h.direction}`, h.gust != null && `gusts ${h.gust} mph`].filter(Boolean).join(', ')}>
              <span className="artemis-wind-time">{h.label}</span>
              {h.arrowDeg != null && (
                <span className="artemis-wind-arrow" style={{ transform: `rotate(${h.arrowDeg}deg)` }} aria-hidden="true"><Icon name="arrow" size={16} /></span>
              )}
              <span className="artemis-wind-speed">{h.speed != null ? h.speed : '–'}{h.gust != null && h.gust > (h.speed || 0) ? <span className="artemis-wind-gust">-{h.gust}</span> : null}</span>
              <span className="artemis-wind-temp">{h.temp != null ? `${h.temp}°` : ''}</span>
            </div>
          ))}
        </div>
        <span className="artemis-hunt-sub">Arrows show where the wind blows · mph</span>
      </div>
    )}
    {card.notes.length > 0 && <div className="artemis-card-facts artemis-weather-notes">{card.notes.join(' ')}</div>}
    <div className="artemis-card-pad" />
  </Card>
)

// Unit alerts (API-3885) ---------------------------------------------------------------------

const ALERTS_SHOWN = 3

// layers: { active: the layer ids on the map, show(ids), hide(ids) } for alerts that carry LayerIds.
const UnitAlert = ({ alert, layers }) => {
  const [open, setOpen] = useState(false)
  const more = !!(alert.detail || alert.url || alert.asOf || alert.layerIds.length)
  const canShow = !!layers && alert.layerIds.length > 0
  const on = canShow && alert.layerIds.every(id => (layers.active || []).includes(id))
  return (
    <li className={`artemis-unit-alert ${alert.warning ? 'artemis-unit-alert-warning' : ''}`}>
      <button type="button" className="artemis-unit-alert-line" onClick={() => more && setOpen(!open)} aria-expanded={more ? open : undefined} disabled={!more}>
        <span className={`artemis-unit-alert-type artemis-unit-alert-type-${alert.type}`}>{alert.label}</span>
        <span className="artemis-flex">{alert.title}</span>
        {more && <Icon name={open ? 'chevronUp' : 'chevronDown'} size={14} />}
      </button>
      {open && (
        <div className="artemis-unit-alert-detail">
          {alert.detail && <p>{alert.detail}</p>}
          {(alert.asOf || alert.source || alert.url) && (
            <span className="artemis-hunt-sub">
              {alert.asOf && `As of ${alert.asOf}`}
              {alert.asOf && (alert.source || alert.url) && ' · '}
              {alert.url
                ? <a href={alert.url} target="_blank" rel="noopener noreferrer">{alert.source || 'Source'}</a>
                : alert.source}
            </span>
          )}
          {canShow && (
            <button type="button" className={`artemis-small-button ${on ? '' : 'artemis-small-button-go'}`} aria-pressed={on}
              onClick={() => (on ? layers.hide(alert.layerIds) : layers.show(alert.layerIds))}>
              {on ? 'Hide from map' : 'Show on map'}
            </button>
          )}
        </div>
      )}
    </li>
  )
}

const UnitAlertGroup = ({ label, alerts, layers }) => {
  const [all, setAll] = useState(false)
  if (alerts.length === 0) return null
  const shown = all ? alerts : alerts.slice(0, ALERTS_SHOWN)
  return (
    <div className="artemis-card-section">
      <span className="artemis-eyebrow">{label}</span>
      <ul className="artemis-unit-alerts">{shown.map(a => <UnitAlert key={a.key} alert={a} layers={layers} />)}</ul>
      {alerts.length > ALERTS_SHOWN && (
        <button type="button" className="artemis-text-more artemis-text-more-block" onClick={() => setAll(!all)}>
          {all ? 'Show less' : `Show ${alerts.length - ALERTS_SHOWN} more`}
        </button>
      )}
    </div>
  )
}

const UnitAlertsCard = ({ card, layers }) => (
  <Card icon="bell" title={card.title} badge={card.badge}>
    <UnitAlertGroup label="Conditions" alerts={card.conditions} layers={layers} />
    <UnitAlertGroup label="In the news" alerts={card.news} layers={layers} />
    <UnitAlertGroup label="Hunters discussing this unit" alerts={card.forum} layers={layers} />
    <div className="artemis-card-pad" />
  </Card>
)

// Climate and conditions (API-3886, API-3889) -------------------------------------------------

const BandPick = ({ bands, value, onChange }) => (bands.length > 1 ? (
  <div className="artemis-card-facts artemis-band-pick">
    <label>
      Elevation
      <select value={value} onChange={e => onChange(e.target.value)}>
        {bands.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
      </select>
    </label>
  </div>
) : null)

const Line = ({ label, text }) => (text ? <div className="artemis-card-note"><span className="artemis-card-note-label">{label}</span><div className="artemis-card-text">{text}</div></div> : null)

const sentence = s => (s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : s)

const UnitClimateCard = ({ card }) => {
  const [band, setBand] = useState(card.band)
  const current = card.bands.find(b => b.key === band) || card.bands[0] || null
  return (
    <Card icon="sun" title={card.title} tone="map">
      <BandPick bands={card.bands} value={current ? current.key : ''} onChange={setBand} />
      {current && (
        <div className="artemis-climate-chart">
          <ClimateChart months={current.months} highlight={card.months} />
          <span className="artemis-hunt-sub">Average high and low · dashed: records · % of days with rain or snow, and with snow on the ground</span>
        </div>
      )}
      {current && card.normals[current.key] && <div className="artemis-card-facts">{sentence(card.normals[current.key])}</div>}
      {(card.wind || card.sky || card.elevation) && (
        <div className="artemis-card-section">
          <Line label="Wind" text={card.wind} />
          <Line label="Sky" text={card.sky} />
          <Line label="Elevation" text={card.elevation} />
        </div>
      )}
      <div className="artemis-hunt-card-footer artemis-hunt-card-footer-rule">
        <span className="artemis-flex">1991–2020 normals, not a forecast{card.range ? ` · ${card.range}` : ''}</span>
      </div>
      {card.sources.length > 0 && <div className="artemis-card-section artemis-card-section-end"><SourceLinks sources={card.sources} label="Data" /></div>}
    </Card>
  )
}

const UnitConditionsCard = ({ card }) => {
  const [band, setBand] = useState(card.band)
  const current = card.bands.find(b => b.key === band) || card.bands[0] || null
  return (
    <Card icon="sun" title={card.title} tone="map">
      <BandPick bands={card.bands} value={current ? current.key : ''} onChange={setBand} />
      {current && current.figures.length > 0 && (
        <div className="artemis-card-section">
          <span className="artemis-eyebrow">Against normal{current.weatherDays ? ` · ${current.weatherDays} days` : ''}</span>
          <div className="artemis-figures">
            {current.figures.map(f => (
              <span key={f.label} className="artemis-figure">
                <strong>{f.value}</strong> {f.label}
                {f.normal && <span className={`artemis-figure-normal ${f.up == null ? '' : f.up ? 'artemis-up' : 'artemis-down'}`}> ({f.normal})</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      {current && current.days.length > 0 && (
        <div className="artemis-card-section">
          <span className="artemis-eyebrow">Last {current.days.length} days</span>
          <div className="artemis-wind-strip artemis-days-strip" style={{ gridTemplateColumns: `repeat(${current.days.length}, minmax(0, 1fr))` }}>
            {current.days.map(d => (
              <div key={d.key} className="artemis-wind-hour" title={d.title}>
                <span className="artemis-wind-time">{d.label}</span>
                <span className="artemis-wind-speed">{d.high != null ? `${d.high}°` : '–'}</span>
                <span className="artemis-wind-temp">{d.low != null ? `${d.low}°` : ''}</span>
                <span className="artemis-wind-gust">{d.snow != null && d.snow > 0 ? `${d.snow} in` : d.precip > 0 ? `${d.precip}″` : ''}</span>
              </div>
            ))}
          </div>
          <span className="artemis-hunt-sub">Day of the month · high over low · snow depth, or rain</span>
        </div>
      )}
      {card.drought && (
        <div className="artemis-card-section">
          <span className="artemis-eyebrow">Drought{card.drought.asOf ? ` · ${card.drought.asOf}` : ''}</span>
          {card.drought.rows.length > 0 && <Bars rows={card.drought.rows} />}
          {card.drought.weeks && <span className="artemis-hunt-sub">{card.drought.weeks}</span>}
        </div>
      )}
      {(card.through || card.sources.length > 0) && (
        <div className="artemis-hunt-card-footer artemis-hunt-card-footer-rule artemis-card-footer-stack">
          {card.through && <span className="artemis-flex">{sentence(card.through)}</span>}
          <SourceLinks sources={card.sources} label="Data" />
        </div>
      )}
    </Card>
  )
}

// Draw systems (API-3890) -------------------------------------------------------------------

const DrawSystemCard = ({ card }) => (
  <Card icon="reg" title={card.title}>
    {card.notes && <div className="artemis-card-facts">{card.notes}</div>}
    {card.species.map(s => (
      <div key={s.key} className="artemis-card-section artemis-draw-species">
        <div className="artemis-card-line">
          <span>{s.name}{s.subSpecies && <span className="artemis-hunt-sub"> · {s.subSpecies}</span>}</span>
          {s.drawType && <span className="artemis-hunt-tag artemis-hunt-tag-draw">{s.drawType}</span>}
        </div>
        {s.groups && <span className="artemis-hunt-sub">Applicant pools: {s.groups}</span>}
        <Clamp text={s.rule} />
        {s.source && <a className="artemis-card-link artemis-card-source" href={s.source} target="_blank" rel="noopener noreferrer">Agency rules<Icon name="external" size={13} /></a>}
      </div>
    ))}
    {card.more > 0 && <div className="artemis-hunt-more">+{card.more} more species</div>}
    <div className="artemis-hunt-card-footer artemis-hunt-card-footer-rule">
      <span className="artemis-flex">Rules change by year</span>
      {card.applyUrl && <ExternalLink href={card.applyUrl}>Apply</ExternalLink>}
    </div>
  </Card>
)

// Hunt Planner's hunts (API-3887) -------------------------------------------------------------

// onOpenPlanner(selection): sets the planner up for the list's state, species and hunt type.
const HuntListCard = ({ card, onOpenPlanner }) => (
  <Card icon="reg" title={card.title}>
    {card.sub && <div className="artemis-card-facts">{card.sub}</div>}
    <ul className="artemis-odds-list">
      {card.hunts.map(h => (
        <li key={h.key}>
          <span className="artemis-flex">
            <span className="artemis-hunt-name">
              {h.name}
              {h.tag && <span className={`artemis-hunt-tag ${h.tag.draw ? 'artemis-hunt-tag-draw' : ''}`}>{h.tag.label}</span>}
            </span>
            <span className="artemis-hunt-sub">{[h.code, h.unit, h.dates].filter(Boolean).join(' · ')}</span>
            {(h.applyFor || h.flags.length > 0) && <span className="artemis-hunt-sub">{[h.applyFor && `Apply for ${h.applyFor}`, ...h.flags].filter(Boolean).join(' · ')}</span>}
          </span>
          {(h.minPoints || h.success) && (
            <span className="artemis-hunt-sub artemis-odds-note artemis-odds-stack">
              {[h.minPoints, h.success].filter(Boolean).map(t => <span key={t}>{t}</span>)}
            </span>
          )}
        </li>
      ))}
    </ul>
    {card.more + card.beyond > 0 && <div className="artemis-hunt-more">+{card.more + card.beyond} more {card.more + card.beyond === 1 ? 'hunt' : 'hunts'}</div>}
    <div className="artemis-hunt-card-footer artemis-hunt-card-footer-rule">
      <span className="artemis-flex">As Hunt Planner lists them</span>
      {card.planner && onOpenPlanner && (
        <button type="button" className="artemis-small-button artemis-small-button-go" onClick={() => onOpenPlanner(card.planner)}>Show in Hunt Planner</button>
      )}
    </div>
  </Card>
)

const HuntDetailsCard = ({ card, onUpgrade }) => (
  <Card icon="reg" title={card.title} badge={card.tag ? card.tag.label : null}>
    {card.facts.length > 0 && <div className="artemis-card-facts">{card.facts.join(' · ')}</div>}
    {card.flags.length > 0 && <div className="artemis-card-facts artemis-hunt-sub">{card.flags.join(' · ')}</div>}
    {card.notes && <div className="artemis-card-section"><Clamp text={card.notes} /></div>}
    {card.seasons.length > 1 && (
      <div className="artemis-card-section">
        <span className="artemis-eyebrow">Seasons on this tag</span>
        {card.seasons.map(s => (
          <div key={s.key} className="artemis-card-line"><span>{s.name}{s.notes && <span className="artemis-hunt-sub"> · {s.notes}</span>}</span><span>{s.dates}</span></div>
        ))}
      </div>
    )}
    {card.odds.length > 0 && <div className="artemis-card-section"><span className="artemis-eyebrow">Odds by points</span><Bars rows={card.odds} /></div>}
    {card.locked && <Upgrade text="Draw odds and success rates need Hunt Research" onUpgrade={onUpgrade} />}
    <div className="artemis-card-pad" />
  </Card>
)

// Observed weather (API-3888), shaped like the forecast card ------------------------------------

const WeatherObservationCard = ({ card }) => (
  <Card icon="sun" title={card.title} tone="map">
    <div className="artemis-weather-days">
      {card.rows.map(r => (
        <div key={r.key} className="artemis-weather-day artemis-observation">
          <span className="artemis-weather-name">{r.when}</span>
          {r.found
            ? (
              <>
                <span className="artemis-weather-temp">{r.temp != null ? `${r.temp}°` : ''}{r.feelsLike != null && r.feelsLike !== r.temp ? <span> feels {r.feelsLike}°</span> : null}</span>
                <span className="artemis-weather-cond">{r.weather || ''}</span>
                <span className="artemis-weather-meta">
                  {[
                    r.high != null && r.low != null ? `${r.high}° / ${r.low}° over 24 h` : null,
                    r.wind ? `wind ${r.wind}${r.gust != null ? `, gusts ${r.gust}` : ''}` : null,
                    r.precip ? `${r.precip} precip` : null,
                    r.pressure,
                    r.humidity != null ? `${r.humidity}% humidity` : null
                  ].filter(Boolean).join(' · ')}
                </span>
              </>
            )
            : <span className="artemis-weather-cond artemis-hunt-sub">No observation for this hour</span>}
        </div>
      ))}
    </div>
    {card.notes.length > 0 && <div className="artemis-card-facts artemis-weather-notes">{card.notes.join(' ')}</div>}
    <div className="artemis-card-pad" />
  </Card>
)

// Deer activity (API-3891): the HuntWind card (WEB-7158) for the point Artemis was asked about.
const DeerActivityCard = ({ card, now }) => (
  <section className="artemis-hunt-card artemis-card-tone-map artemis-deer-card" aria-label="Deer activity">
    <DeerActivityView days={card.days} where="here" now={now} />
  </section>
)

const UpgradeCard = ({ card, onUpgrade }) => (
  <Card icon="lock" title="Hunt data">
    <Upgrade text={`${card.tools.length ? `${card.tools.join(', ')} ${card.tools.length === 1 ? 'needs' : 'need'}` : 'This needs'} a hunt data subscription`} onUpgrade={onUpgrade} />
  </Card>
)

const CARDS = {
  regulations: RegulationsCard,
  draw: DrawCard,
  draw_odds: DrawOddsCard,
  unit_info: UnitInfoCard,
  units_at_point: UnitsAtPointCard,
  harvest_statistics: HarvestCard,
  terrain_analysis: TerrainCard,
  viewshed: ViewshedCard,
  weather_forecast: WeatherCard,
  weather_observation: WeatherObservationCard,
  unit_alerts: UnitAlertsCard,
  unit_climate: UnitClimateCard,
  unit_conditions: UnitConditionsCard,
  draw_system: DrawSystemCard,
  hunt_list: HuntListCard,
  hunt_details: HuntDetailsCard,
  deer_activity: DeerActivityCard,
  upgrade: UpgradeCard
}

const NO_OVERLAY = { key: null, show: () => {}, hide: () => {} }

// layers: { active, show(ids), hide(ids) } for unit alerts' map buttons; onOpenPlanner(selection) for hunt lists.
const ResultCards = ({ message, now, overlay = NO_OVERLAY, onAsk, onUpgrade, skillActions, onManageAlert, layers, onOpenPlanner }) => {
  if (message.status === 'streaming') return null
  const cards = resultCards(message, now)
  if (cards.length === 0) return null
  return cards.map(({ key, kind, card }) => {
    if (kind === 'user_skill') {
      if (!skillActions) return null
      return <UserSkillCard key={key} card={card} current={skillActions.current(card.skill.Id)}
        onEdit={skillActions.onEdit} onToggle={skillActions.onToggle} onDelete={skillActions.onDelete} />
    }
    if (kind === 'alert') return <SavedAlertCard key={key} card={card} onManage={onManageAlert} />
    const Component = CARDS[kind]
    const cardKey = `${message.id}:${key}`
    return Component
      ? <Component key={key} cardKey={cardKey} card={card} overlay={overlay} onAsk={onAsk} onUpgrade={onUpgrade} layers={layers} onOpenPlanner={onOpenPlanner} now={now} />
      : null
  })
}

export default ResultCards
